import { useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import * as icons from "react-bootstrap-icons";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { GetEmailVerification, VerifyEmail as ConfirmEmailLink } from "../../redux/auth-reducer/auth-service";
import { GetToken, homeForRole, readApiMessage, setStoredEmailVerified } from "../../utils/helper/helper";
import { VERIFY_STATE, verifyPageText } from "../../utils/helper/emailVerification";
import { useSendVerificationEmail } from "../../hooks/useEmailVerification";

/**
 * The page the link in every verification email opens: /VerifyEmail?token=...
 *
 * No sign-in: the link's token names the account, and the confirmation carries no session. Opening
 * the page changes nothing - mail security scanners open every link in an email - until the person
 * presses "Confirm my email". A new sign-up can then sign in; an account signed in on this browser
 * re-reads its own state, so its banner goes (the link may have been another account's).
 *
 * A link the server refuses - used, expired, not ours - says why. Signed in, the page offers a new
 * link; otherwise the sign-in screen does, after its refusal.
 */
export const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [state, setState] = useState(token ? VERIFY_STATE.READY : VERIFY_STATE.MISSING);
  const [failure, setFailure] = useState("");
  const [busy, setBusy] = useState(false);
  const { send, sending } = useSendVerificationEmail();

  // "Signed in" means a token and a role.
  const role = localStorage.getItem("role");
  const home = GetToken() && role ? homeForRole(role) : null;

  const confirm = async () => {
    setBusy(true);
    const res = await ConfirmEmailLink(token);
    setBusy(false);

    if (res?.data?.status === true) {
      setState(VERIFY_STATE.DONE);
      if (home) {
        GetEmailVerification().then((own) => {
          if (own?.data?.status === true) setStoredEmailVerified(Boolean(own.data.emailVerified));
        });
      }
      return;
    }
    if (!res) return; // no reply: apiClient has already said so, and the button stays.
    if (res.status >= 500) {
      toast.error(readApiMessage(res));
      return;
    }
    setFailure(readApiMessage(res));
    setState(VERIFY_STATE.FAILED);
  };

  const { title, text } = verifyPageText(state, failure);
  const refused = state === VERIFY_STATE.FAILED || state === VERIFY_STATE.MISSING;

  return (
    <section className="payment-success unsubscribe-page">
      <Container>
        <Row className="justify-content-center">
          <Col md={8} lg={6} className="text-center">
            {state === VERIFY_STATE.DONE ? (
              <icons.Check2Circle className="payment-check" />
            ) : refused ? (
              <icons.ExclamationTriangle className="payment-check-cancel" />
            ) : (
              <icons.EnvelopeCheck className="unsubscribe-icon" />
            )}
            <h2 className="mt-3">{title}</h2>
            <p>{text}</p>

            {state === VERIFY_STATE.READY && (
              <Button className="mt-3" onClick={confirm} disabled={busy}>
                Confirm my email
              </Button>
            )}
            {refused && home && (
              <Button className="mt-3" variant="outline-primary" onClick={send} disabled={sending}>
                Send a new link
              </Button>
            )}

            <div className="mt-3">
              {home ? (
                <Link className="btn btn-link" to={home}>
                  Go to my dashboard
                </Link>
              ) : (
                <Link className="btn btn-link" to="/Login">
                  Sign in
                </Link>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};
